import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  newsItems,
  dashboardMyWorkspace,
  dashboardSharedWorkspace,
  dashboardShortcutPills,
  dashboardCompetitorActivity,
  dashboardWordCloudTerms,
  dashboardSignalsByMonth,
  dashboardWinLossMix,
  dashboardShareOfVoice,
  timelineDetailByNewsId,
} from '../mockData';
import { useIntel } from '../IntelContext';

const SHARED_PLACEHOLDER = (name) =>
  `Showing placeholder layout for "${name}" — pin widgets once this shared dashboard is hydrated from mock sync.`;

const WIDGET_IDS = [
  ['Competitor Analysis', 'dash-widget-competitor'],
  ['New Products / Enhancements', 'dash-widget-wordcloud'],
  ['Customers by Location', 'dash-widget-timeline'],
  ['Lose (Win-Loss) Reasons', 'dash-widget-newsfeed'],
];

function findWidgetAnchor(label) {
  const pair = WIDGET_IDS.find(([k]) =>
    label.toLowerCase().includes(k.split(' ')[0].toLowerCase()),
  );
  if (!pair && label.includes('Customer')) return 'dash-widget-newsfeed';
  return pair ? pair[1] : 'dash-widget-competitor';
}

function DashboardMonthlyBars({ data }) {
  const w = 400;
  const h = 168;
  const pad = { l: 36, r: 10, b: 32, t: 14 };
  const max = Math.max(...data.map((d) => d.value), 1);
  const innerW = w - pad.l - pad.r;
  const innerH = h - pad.t - pad.b;
  const gap = 6;
  const bw = data.length ? (innerW - gap * (data.length - 1)) / data.length : 0;
  return (
    <div className="dash-chart-svg-wrap" aria-hidden>
      <svg
        width="100%"
        viewBox={`0 0 ${w} ${h}`}
        className="dash-chart-svg"
        preserveAspectRatio="xMidYMid meet"
      >
        {[0, 0.5, 1].map((t) => (
          <line
            key={t}
            x1={pad.l}
            x2={w - pad.r}
            y1={pad.t + (1 - t) * innerH}
            y2={pad.t + (1 - t) * innerH}
            stroke="#e2e8f0"
            strokeWidth="1"
          />
        ))}
        {data.map((d, i) => {
          const barH = (innerH * d.value) / max;
          const x = pad.l + i * (bw + gap);
          const y = pad.t + innerH - barH;
          return (
            <g key={d.month}>
              <rect x={x} y={y} width={bw} height={barH} rx={4} fill="#2563eb" opacity="0.88" />
              <text
                x={x + bw / 2}
                y={h - 8}
                textAnchor="middle"
                fontSize="10"
                fill="#64748b"
                fontWeight="600"
              >
                {d.month}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function DashboardWinLossStack({ segments }) {
  const total = segments.reduce((s, x) => s + x.value, 0) || 1;
  return (
    <div className="dash-winloss-stack" role="img" aria-label="Win-loss mix mock chart">
      <div className="dash-winloss-bar">
        {segments.map((s) => (
          <div
            key={s.label}
            className="dash-winloss-seg"
            style={{
              width: `${(100 * s.value) / total}%`,
              backgroundColor: s.color,
            }}
            title={`${s.label}: ${Math.round((100 * s.value) / total)}%`}
          />
        ))}
      </div>
      <ul className="dash-winloss-legend">
        {segments.map((s) => (
          <li key={s.label} className="dash-winloss-legend-item">
            <span className="dash-winloss-swatch" style={{ backgroundColor: s.color }} />
            <span className="dash-winloss-name">{s.label}</span>
            <span className="dash-winloss-pct">{Math.round((100 * s.value) / total)}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function DashboardShareBars({ rows }) {
  const max = Math.max(...rows.map((r) => r.value), 1);
  return (
    <div className="dash-share-bars" role="list">
      {rows.map((r) => (
        <div key={r.label} className="dash-share-row" role="listitem">
          <span className="dash-share-label">{r.label}</span>
          <div className="dash-share-track">
            <div
              className="dash-share-fill"
              style={{
                width: `${(100 * r.value) / max}%`,
                backgroundColor: r.color,
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

const GmmDashboardPage = ({ intelBridge }) => {
  const { toast, navigate } = useIntel();
  const [activeWorkspace, setActiveWorkspace] = useState('strategy');
  const [workspaceKind, setWorkspaceKind] = useState('my');
  const [sharedPlaceholder, setSharedPlaceholder] = useState('');

  const [myWsList, setMyWsList] = useState(() => dashboardMyWorkspace.map((x) => ({ ...x })));

  const [newWsModal, setNewWsModal] = useState(false);
  const [newWsName, setNewWsName] = useState('');

  const [refreshSpin, setRefreshSpin] = useState(false);
  const [widgetModal, setWidgetModal] = useState(false);
  const [moreDrop, setMoreDrop] = useState(false);

  const [timelineDrawer, setTimelineDrawer] = useState(null);

  const appliedBridge = useRef(-1);

  useEffect(() => {
    const n = intelBridge?.nonce;
    const wid = intelBridge?.dashboardWorkspaceId;
    if (typeof n !== 'number' || n === appliedBridge.current) return;
    if (wid) {
      appliedBridge.current = n;
      setActiveWorkspace(wid);
      setWorkspaceKind('my');
      setSharedPlaceholder('');
    }
  }, [intelBridge]);

  useEffect(() => {
    let t;
    if (refreshSpin) t = window.setTimeout(() => setRefreshSpin(false), 1500);
    return () => window.clearTimeout(t);
  }, [refreshSpin]);

  const timelineEvents = useMemo(() => {
    return [...newsItems]
      .sort((a, b) => {
        const ta = new Date(a.date).getTime();
        const tb = new Date(b.date).getTime();
        const na = Number.isFinite(ta) ? ta : 0;
        const nb = Number.isFinite(tb) ? tb : 0;
        return nb - na;
      })
      .map((n) => ({
        id: n.id,
        date: n.date,
        title: n.title,
        company: n.company.name,
      }));
  }, []);

  const breadcrumbWorkspace =
    workspaceKind === 'shared'
      ? dashboardSharedWorkspace.find((w) => w.id === activeWorkspace)?.label || 'Shared'
      : myWsList.find((w) => w.id === activeWorkspace)?.label || 'Workspace';

  const shortcutScroll = (label) => {
    if (label === 'More') return;
    const id = findWidgetAnchor(label);
    window.requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    toast('Widget highlighted');
  };

  const downloadActivity = () => {
    const text = dashboardCompetitorActivity.join('\n\n');
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'competitor-activity-summary.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  async function clipboardCopy(txt) {
    try {
      await navigator.clipboard.writeText(txt);
      toast('Link copied');
    } catch {
      toast('Link copied');
      toast(txt);
    }
  }

  const createWs = () => {
    const name = newWsName.trim();
    if (!name) {
      toast('Enter a workspace name');
      return;
    }
    const id = `ws-${Date.now()}`;
    setMyWsList((list) => [...list, { id, label: name }]);
    setActiveWorkspace(id);
    setWorkspaceKind('my');
    setSharedPlaceholder('');
    setNewWsModal(false);
    setNewWsName('');
    toast('Workspace created');
  };

  const selectShared = (row) => {
    setWorkspaceKind('shared');
    setActiveWorkspace(row.id);
    setSharedPlaceholder(SHARED_PLACEHOLDER(row.label));
    toast(`${row.label} active`);
  };

  return (
    <div className="gmm-dashboard">
      {newWsModal ? (
        <div
          className="gmm-modal-backdrop"
          role="presentation"
          onClick={() => setNewWsModal(false)}
        >
          <div className="gmm-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="gmm-modal-title">Create workspace</h3>
            <input
              className="gmm-input"
              value={newWsName}
              onChange={(e) => setNewWsName(e.target.value)}
              placeholder="Workspace name"
            />
            <div className="gmm-modal-actions">
              <button type="button" className="gmm-btn-muted" onClick={() => setNewWsModal(false)}>
                Cancel
              </button>
              <button type="button" className="gmm-btn-primary" onClick={createWs}>
                Create
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {widgetModal ? (
        <div
          className="gmm-modal-backdrop"
          role="presentation"
          onClick={() => setWidgetModal(false)}
        >
          <div className="gmm-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="gmm-modal-title">Add widget</h3>
            <p className="gmm-muted">
              Competitor KPI / News density / Regulatory heatmap — pick one mock slot.
            </p>
            {['Competitor KPI Tile', 'News Density Gauge', 'Regulatory Heat'].map((w) => (
              <button
                key={w}
                type="button"
                className="gmm-dropdown-item gmm-dropdown-item-block"
                onClick={() => {
                  toast(`${w}: Widget added`);
                  setWidgetModal(false);
                }}
              >
                + {w}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {timelineDrawer != null ? (
        <>
          <div
            className="gmm-drawer-scrim fade-in"
            role="presentation"
            onClick={() => setTimelineDrawer(null)}
          />
          <aside className="gmm-drawer-side open">
            <div className="gmm-drawer-side-header">
              <h3>{timelineDrawer.title}</h3>
              <button
                type="button"
                className="gmm-drawer-close"
                aria-label="Close"
                onClick={() => setTimelineDrawer(null)}
              >
                ×
              </button>
            </div>
            <p className="gmm-muted">
              {timelineDrawer.company} · {timelineDrawer.date}
            </p>
            <div className="gmm-drawer-body">
              {timelineDetailByNewsId[timelineDrawer.id] ||
                'Additional regulatory and earnings context will render here once the intelligence graph sync completes.'}
            </div>
          </aside>
        </>
      ) : null}

      <aside className="dashboard-sidebar">
        <div className="dashboard-sidebar-header">
          <span className="dashboard-sidebar-title">Workspaces</span>
          <button type="button" className="dashboard-new-btn" onClick={() => setNewWsModal(true)}>
            + New
          </button>
        </div>

        <div className="dashboard-sidebar-section">
          <div className="dashboard-sidebar-label">MY WORKSPACE</div>
          <nav className="dashboard-nav">
            {myWsList.map((ws) => (
              <button
                key={ws.id}
                type="button"
                className={`dashboard-nav-link ${
                  activeWorkspace === ws.id && workspaceKind === 'my' ? 'active' : ''
                }`}
                onClick={() => {
                  setWorkspaceKind('my');
                  setActiveWorkspace(ws.id);
                  setSharedPlaceholder('');
                  toast(`${ws.label} workspace`);
                }}
              >
                {ws.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="dashboard-sidebar-section">
          <div className="dashboard-sidebar-label">SHARED DASHBOARD</div>
          <nav className="dashboard-nav">
            {dashboardSharedWorkspace.map((ws) => (
              <button
                key={ws.id}
                type="button"
                className={`dashboard-nav-link secondary ${
                  activeWorkspace === ws.id && workspaceKind === 'shared' ? 'active-shared' : ''
                }`}
                onClick={() => selectShared(ws)}
              >
                {ws.label}
              </button>
            ))}
          </nav>
        </div>
      </aside>

      <main className="dashboard-main">
        <div className="dashboard-main-inner">
          <div className="dashboard-top-row">
            <div className="dashboard-breadcrumbs">
              <span className="crumb">{breadcrumbWorkspace}</span>
              <span className="crumb-sep">/</span>
              <span className="crumb crumb-current">Competitive Edge Dashboard</span>
            </div>
            <div className="dashboard-meta-actions">
              <span className="dashboard-updated">Last updated: 28 Apr 2026</span>
              <button
                type="button"
                title="Refresh"
                className={`dashboard-icon-action ${refreshSpin ? 'dash-spin' : ''}`}
                onClick={() => {
                  setRefreshSpin(true);
                  toast('Dashboard refreshed');
                }}
              >
                ↻
              </button>
              <button type="button" title="Share" className="dashboard-icon-action" onClick={() => clipboardCopy(window.location.href)}>
                ↗
              </button>
              <button type="button" className="dashboard-add-btn" onClick={() => setWidgetModal(true)}>
                + Add
              </button>
            </div>
          </div>

          {workspaceKind === 'shared' && sharedPlaceholder ? (
            <div className="dash-shared-banner">{sharedPlaceholder}</div>
          ) : null}

          <div className="dashboard-shortcuts">
            {dashboardShortcutPills.map((label) =>
              label === 'More' ? (
                <div key={label} className="dash-more-wrap">
                  <button type="button" className="dashboard-shortcut-pill" onClick={() => setMoreDrop((v) => !v)}>
                    {label}
                  </button>
                  {moreDrop ? (
                    <div className="gmm-mini-panel dash-more-panel">
                      {['Market Share', 'Pricing Analysis', 'Export Opportunities'].map((o) => (
                        <button
                          key={o}
                          type="button"
                          className="gmm-dropdown-item"
                          onClick={() => {
                            toast(`${o}: mock dataset loading…`);
                            setMoreDrop(false);
                          }}
                        >
                          {o}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : (
                <button
                  key={label}
                  type="button"
                  className="dashboard-shortcut-pill"
                  onClick={() => shortcutScroll(label)}
                >
                  {label}
                </button>
              ),
            )}
          </div>

          <div className="dashboard-section-label">Strategic View / Competitive View</div>

          <div id="dash-widget-charts" className="dashboard-charts-row">
            <section className="dashboard-chart-card">
              <header className="dashboard-chart-card-header">
                <span className="dashboard-chart-card-title">Tracked signals (6 mo.)</span>
                <span className="dashboard-chart-card-sub">Saved search · mock volume</span>
              </header>
              <DashboardMonthlyBars data={dashboardSignalsByMonth} />
              <p className="dashboard-chart-footnote">Indexed updates, tenders, and regulatory notices in your workspace filters.</p>
            </section>
            <section className="dashboard-chart-card">
              <header className="dashboard-chart-card-header">
                <span className="dashboard-chart-card-title">Win–loss mix</span>
                <span className="dashboard-chart-card-sub">Deals tagged in CRM (demo)</span>
              </header>
              <DashboardWinLossStack segments={dashboardWinLossMix} />
              <p className="dashboard-chart-footnote">Stacked share of outcomes for glass-lined and allied bids in the last 90 days.</p>
            </section>
            <section className="dashboard-chart-card">
              <header className="dashboard-chart-card-header">
                <span className="dashboard-chart-card-title">Share of voice</span>
                <span className="dashboard-chart-card-sub">Mentions vs. GMM baseline</span>
              </header>
              <DashboardShareBars rows={dashboardShareOfVoice} />
              <p className="dashboard-chart-footnote">Relative mention intensity in news and tender text (normalized).</p>
            </section>
          </div>

          <div className="dashboard-widgets-top">
            <section id="dash-widget-competitor" className="dashboard-widget dashboard-widget--accent-competitor">
              <header className="dashboard-widget-header">
                <div className="dashboard-widget-title-row">
                  <span className="dashboard-widget-title">Competitor Activity Widget</span>
                  <span className="dashboard-widget-pipe">|</span>
                  <span className="dashboard-widget-sub">Saved Search</span>
                </div>
                <p className="dashboard-widget-desc">
                  Recent strategic moves by glass-lined equipment competitors and import alternatives relevant
                  to GMM India&apos;s footprint.
                </p>
              </header>
              <div className="dashboard-widget-body competitor-activity-body">
                {dashboardCompetitorActivity.map((para, idx) => (
                  <p key={idx} className="dashboard-activity-para">
                    {para}
                  </p>
                ))}
              </div>
              <footer className="dashboard-widget-footer">
                <span className="dashboard-attachment">Latest_Updates.pdf</span>
                <button type="button" className="dashboard-download-btn" onClick={downloadActivity}>
                  Download
                </button>
              </footer>
            </section>

            <section id="dash-widget-newsfeed" className="dashboard-widget dashboard-widget--accent-newsfeed">
              <header className="dashboard-widget-header">
                <div className="dashboard-widget-title-row">
                  <span className="dashboard-widget-title">Newsfeed Widget</span>
                  <span className="dashboard-widget-pipe">|</span>
                  <span className="dashboard-widget-sub">Saved Search</span>
                </div>
                <p className="dashboard-widget-desc">
                  Capex, PLI, earnings, and regulatory signals across tracked customers and institutions.
                </p>
              </header>
              <ul className="dashboard-newsfeed-list">
                {newsItems.slice(0, 5).map((item) => (
                  <li key={item.id} className="dashboard-newsfeed-item">
                    <span className="dashboard-newsfeed-date">{item.date}</span>
                    <span className="dashboard-newsfeed-co">{item.company.name}</span>
                    <span className="dashboard-newsfeed-head">{item.title}</span>
                  </li>
                ))}
              </ul>
              <footer className="dashboard-widget-footer plain">
                <button
                  type="button"
                  className="dashboard-view-all"
                  onClick={() =>
                    navigate('newsfeed', { newsfeedSubTab: 'key', newsfeedSearch: '' })
                  }
                >
                  View all →
                </button>
              </footer>
            </section>

            <section id="dash-widget-wordcloud" className="dashboard-widget dashboard-widget--wordcloud dashboard-widget--accent-wordcloud">
              <header className="dashboard-widget-header">
                <div className="dashboard-widget-title-row">
                  <span className="dashboard-widget-title">Word Cloud Widget</span>
                  <span className="dashboard-widget-pipe">|</span>
                  <span className="dashboard-widget-sub">Saved Search</span>
                </div>
                <p className="dashboard-widget-desc">
                  Recurring themes from filings, calls, and regulatory notices in the tracked universe.
                </p>
              </header>
              <div className="dashboard-wordcloud">
                {dashboardWordCloudTerms.map((t, i) => (
                  <button
                    key={`${t.term}-${i}`}
                    type="button"
                    className={`dashboard-wc-term dashboard-wc-weight-${t.weight} dash-wc-btn`}
                    onClick={() =>
                      navigate('newsfeed', { newsfeedSearch: t.term.replace(/-/g, ' ') })
                    }
                  >
                    {t.term}
                  </button>
                ))}
              </div>
            </section>
          </div>

          <section id="dash-widget-timeline" className="dashboard-widget dashboard-widget--timeline dashboard-widget--accent-timeline">
            <header className="dashboard-widget-header">
              <div className="dashboard-widget-title-row">
                <span className="dashboard-widget-title">Timeline Widget</span>
                <span className="dashboard-widget-pipe">|</span>
                <span className="dashboard-widget-sub">Saved Search</span>
              </div>
              <p className="dashboard-widget-desc">
                Key competitor and customer signals over time — filings, press, notifications, and calls.
              </p>
            </header>
            <div className="dashboard-timeline">
              {timelineEvents.map((ev, idx) => (
                <button
                  key={ev.id}
                  type="button"
                  className="dashboard-timeline-row dash-timeline-btn"
                  onClick={() =>
                    setTimelineDrawer({ id: ev.id, title: ev.title, company: ev.company, date: ev.date })
                  }
                >
                  <div className="dashboard-timeline-marker-wrap">
                    <span className="dashboard-timeline-dot" />
                    {idx < timelineEvents.length - 1 ? <span className="dashboard-timeline-line" /> : null}
                  </div>
                  <div className="dashboard-timeline-content">
                    <div className="dashboard-timeline-date">{ev.date}</div>
                    <div className="dashboard-timeline-company">{ev.company}</div>
                    <div className="dashboard-timeline-title">{ev.title}</div>
                  </div>
                </button>
              ))}
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default GmmDashboardPage;
